import React, { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';

import { useElementScrollRestoration, useRouter } from '@tanstack/react-router';
import classNames from 'classnames';
import { getPageScrollRestorationKey, PAGE_SCROLL_RESTORATION_ID } from '../pageScrollRestoration';
import StaticContent from './StaticContent';

export interface Props {
  className?: string;
  children?: React.ReactNode;
  staticContent?: boolean;
  disableBottomPadding?: boolean;
}

const SETTLING_DURATION_MS = 600;
const READER_TOOK_OVER_EVENTS = ['wheel', 'touchstart', 'pointerdown'] as const;
const READER_SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

const isScrollRestorationEnabledForCurrentRoute = (router: ReturnType<typeof useRouter>): boolean => {
  const { scrollRestoration } = router.options;
  return typeof scrollRestoration === 'function'
    ? scrollRestoration({ location: router.latestLocation })
    : Boolean(scrollRestoration);
};

const holdScrollPositionUntilPageSettles = (element: HTMLElement, target: number): (() => void) => {
  const settledAt = performance.now() + SETTLING_DURATION_MS;
  let frame = 0;

  const reapplyTarget = () => {
    if (element.scrollTop !== target) element.scrollTop = target;
    if (performance.now() < settledAt) frame = requestAnimationFrame(reapplyTarget);
  };
  const releaseToReader = () => {
    cancelAnimationFrame(frame);
    READER_TOOK_OVER_EVENTS.forEach((event) => window.removeEventListener(event, releaseToReader, true));
    window.removeEventListener('keydown', releaseToReaderOnScrollKey, true);
  };
  const releaseToReaderOnScrollKey = (event: KeyboardEvent) => {
    if (READER_SCROLL_KEYS.has(event.key)) releaseToReader();
  };

  READER_TOOK_OVER_EVENTS.forEach((event) =>
    window.addEventListener(event, releaseToReader, { capture: true, passive: true }),
  );
  window.addEventListener('keydown', releaseToReaderOnScrollKey, { capture: true, passive: true });
  reapplyTarget();

  return releaseToReader;
};

const Content = forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
  const { className, children, staticContent, disableBottomPadding } = props;
  const router = useRouter();
  const scrollEntry = useElementScrollRestoration({
    id: PAGE_SCROLL_RESTORATION_ID,
    getKey: getPageScrollRestorationKey,
  });
  const elementRef = useRef<HTMLDivElement | null>(null);

  const setElementRef = useCallback(
    (element: HTMLDivElement | null) => {
      elementRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref],
  );

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element || !scrollEntry || !isScrollRestorationEnabledForCurrentRoute(router)) return undefined;
    return holdScrollPositionUntilPageSettles(element, scrollEntry.scrollY);
  }, []);

  if (staticContent) {
    return (
      <StaticContent ref={ref} className={className}>
        {children}
      </StaticContent>
    );
  }
  return (
    <main
      ref={setElementRef}
      data-scroll-restoration-id={PAGE_SCROLL_RESTORATION_ID}
      className={classNames('overflow-x-clip', { 'pb-120 md:pb-[0px]': !disableBottomPadding }, className)}
    >
      {children}
    </main>
  );
});
Content.displayName = 'Content';

export default Content;
